import React, { useState } from "react";
import { ArrowLeft, RefreshCw, Copy, Lock, Menu, X, Download, EyeOff, Eye, Clock, Shield, Settings, InfoIcon } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import SEOHelmet from "../../../components/SEOHelmet";

export default function PasswordGenerator() {
  const [length, setLength] = useState(12);
  const [includeUpper, setIncludeUpper] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(false);
  const [excludeSimilar, setExcludeSimilar] = useState(false);
  const [useWords, setUseWords] = useState(false);
  const [customBase, setCustomBase] = useState(""); // NEW ✅ Custom password base

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Add a new state
  const [strengthLevel, setStrengthLevel] = useState(""); // "", "Weak", "Medium", "Strong"
  const [passwordInput, setPasswordInput] = useState("");

  const navigate = useNavigate();

  const { seconds, entropy, display, avgGuesses, guessesPerSecond } =
    estimateCrackTimeDetailed(passwordInput, 1e9);

  let barColor = "bg-green-600";
  if (seconds < 1e-3) barColor = "bg-red-500";
  else if (seconds < 60) barColor = "bg-red-500";
  else if (seconds < 3600 * 24 * 365) barColor = "bg-yellow-500";

  const widthPercent = Math.min(100, Math.log10(seconds + 10) * 10 + 5);


  // Expanded word list (tech, nature, daily-use words)
  const words = [
    "Tiger", "Cloud", "River", "Pixel", "Ocean", "Mango", "Secure",
    "Eagle", "Stone", "Nova", "Tech", "Zenith", "Sky", "Galaxy",
    "Forest", "Cyber", "Shield", "Matrix", "Orbit", "Storm",
    "Crypto", "Quantum", "Falcon", "Solar", "Spark", "Shadow",
    "Neon", "Iron", "Summit", "Volt", "Echo", "Logic",
    "Binary", "Carbon", "Laser", "Drift", "Zen", "Blaze"
  ];

  // // Generate random character password
  // const generateRandomPassword = () => {
  //   const lower = "abcdefghijklmnopqrstuvwxyz";
  //   const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  //   const numbers = "0123456789";
  //   const symbols = "!@#$%^&*()-_=+[]{};:,.<>?";
  //   const similar = "1lIoO0";

  //   let chars = lower;
  //   if (includeUpper) chars += upper;
  //   if (includeNumbers) chars += numbers;
  //   if (includeSymbols) chars += symbols;
  //   if (excludeSimilar) {
  //     chars = chars.split("").filter((ch) => !similar.includes(ch)).join("");
  //   }

  //   if (!chars) return "";

  //   let pwd = "";
  //   for (let i = 0; i < length; i++) {
  //     pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  //   }
  //   return pwd;
  // };

  // // Generate word-based password
  // const generateWordPassword = () => {
  //   let wordCount = Math.max(3, Math.floor(length / 4));
  //   let passWords = [];
  //   for (let i = 0; i < wordCount; i++) {
  //     passWords.push(words[Math.floor(Math.random() * words.length)]);
  //   }
  //   const separators = ["-", "_", ".", ""];
  //   const sep = separators[Math.floor(Math.random() * separators.length)];
  //   let pwd = passWords.join(sep);

  //   if (includeNumbers) pwd += Math.floor(Math.random() * 100);
  //   if (includeSymbols) pwd += "!";

  //   return pwd;
  // };

  const generateRandomPassword = () => {
    let pwdLength = length;
    let chars = "abcdefghijklmnopqrstuvwxyz";

    // If a strength is selected, adjust settings
    let useUpper = includeUpper;
    let useNumbers = includeNumbers;
    let useSymbols = includeSymbols;
    let useExcludeSimilar = excludeSimilar;

    if (strengthLevel === "Weak") {
      pwdLength = Math.max(4, Math.min(6, length));
      useUpper = false;
      useNumbers = false;
      useSymbols = false;
    } else if (strengthLevel === "Medium") {
      pwdLength = Math.max(7, Math.min(10, length));
      useUpper = includeUpper;
      useNumbers = includeNumbers;
      useSymbols = includeSymbols;
    } else if (strengthLevel === "Strong") {
      pwdLength = Math.max(12, length); // longer length
      useUpper = true;      // force uppercase
      useNumbers = true;    // force numbers
      useSymbols = true;    // force symbols
      useExcludeSimilar = true; // avoid confusing chars
    }

    if (useUpper) chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (useNumbers) chars += "0123456789";
    if (useSymbols) chars += "!@#$%^&*()-_=+[]{};:,.<>?";
    if (useExcludeSimilar) {
      chars = chars.split("").filter((ch) => !"1lIoO0".includes(ch)).join("");
    }

    if (!chars) return "";

    let pwd = "";
    for (let i = 0; i < pwdLength; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  };

  const generateWordPassword = () => {
    let wordCount = Math.max(3, Math.floor(length / 4));

    if (strengthLevel === "Weak") wordCount = 2;
    else if (strengthLevel === "Medium") wordCount = 3;
    else if (strengthLevel === "Strong") wordCount = 4; // more words

    let passWords = [];
    for (let i = 0; i < wordCount; i++) {
      passWords.push(words[Math.floor(Math.random() * words.length)]);
    }

    const separators = ["-", "_", ".", ""];
    const sep = separators[Math.floor(Math.random() * separators.length)];
    let pwd = passWords.join(sep);

    // Force strong: include numbers and symbols if strong
    const addNumbers = strengthLevel === "Strong" ? true : includeNumbers;
    const addSymbols = strengthLevel === "Strong" ? true : includeSymbols;

    if (addNumbers) pwd += Math.floor(Math.random() * 100);
    if (addSymbols) pwd += "!";

    return pwd;
  };



  // Generate custom-based password
  const generateCustomPassword = () => {
    console.log("strengthLevel", strengthLevel)
    if (!customBase) return "";

    let pwd = customBase.replace(/\s+/g, ""); // remove spaces

    const symbols = "@#!$%&";
    const numbers = "0123456789";

    if (strengthLevel === "Weak") {
      // Weak: lowercase + 1 number if missing
      if (!/\d/.test(pwd)) pwd += numbers[Math.floor(Math.random() * numbers.length)];
    }
    else if (strengthLevel === "Medium") {
      const targetLength = Math.max(10, length); // Minimum 10 for Medium, or user-specified if longer

      // Capitalize first letter
      if (!/[A-Z]/.test(pwd)) pwd = pwd.charAt(0).toUpperCase() + pwd.slice(1);

      // Ensure at least one number
      if (!/\d/.test(pwd)) pwd += numbers[Math.floor(Math.random() * numbers.length)];

      // Optional symbol
      if (!/[@#!$%&]/.test(pwd) && includeSymbols) pwd += symbols[Math.floor(Math.random() * symbols.length)];

      // Pad to reach target length
      const allChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789" + (includeSymbols ? symbols : "");
      while (pwd.length < targetLength) {
        pwd += allChars.charAt(Math.floor(Math.random() * allChars.length));
      }
    }

    else if (strengthLevel === "Strong") {
      // Strong: enforce uppercase, numbers, symbols, min length 12
      if (!/[A-Z]/.test(pwd)) pwd = pwd.charAt(0).toUpperCase() + pwd.slice(1);
      if (!/\d/.test(pwd)) pwd += numbers[Math.floor(Math.random() * numbers.length)];
      if (!/[@#!$%&]/.test(pwd)) pwd += symbols[Math.floor(Math.random() * symbols.length)];

      // Pad to 12 characters if too short
      while (pwd.length < 12) {
        const allChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789" + symbols;
        pwd += allChars.charAt(Math.floor(Math.random() * allChars.length));
      }
    }
    else {
      // Normal: just enhance with selected options
      if (includeNumbers && !/\d/.test(pwd)) pwd += numbers[Math.floor(Math.random() * numbers.length)];
      if (includeSymbols && !/[@#!$%&]/.test(pwd)) pwd += symbols[Math.floor(Math.random() * symbols.length)];
      if (includeUpper && !/[A-Z]/.test(pwd)) pwd = pwd.charAt(0).toUpperCase() + pwd.slice(1);
    }

    return pwd;
  };


  // Main generator
const generatePassword = () => {
  let pwd = "";

  if (customBase) {
    pwd = generateCustomPassword();
  } else {
    pwd = useWords ? generateWordPassword() : generateRandomPassword();
  }

  setPassword(pwd);
  setCopied(false);

  // Save to localStorage for history
  const newEntry = { password: pwd, date: new Date().toLocaleString() };
  const existing = JSON.parse(localStorage.getItem("passwordHistory")) || [];
  localStorage.setItem(
    "passwordHistory",
    JSON.stringify([newEntry, ...existing])
  );
};


  const getStrength = (pwd = password) => {
    if (!pwd) return "";

    let strengthPoints = 0;

    // Length points
    if (pwd.length >= 8) strengthPoints++;
    if (pwd.length >= 12) strengthPoints++;

    // Character diversity points
    if (/[A-Z]/.test(pwd)) strengthPoints++;    // uppercase
    if (/\d/.test(pwd)) strengthPoints++;       // number
    if (/[@#!$%&]/.test(pwd)) strengthPoints++; // symbol

    // Determine strength
    if (strengthPoints <= 2) return "Weak";
    if (strengthPoints <= 4) return "Medium";
    return "Strong";
  };


  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


// =================== Helper Functions ============

function formatDurationDetailed(seconds) {
  if (!isFinite(seconds) || seconds <= 0) return { label: "Instant", breakdown: null };

  const secInMinute = 60;
  const secInHour = 3600;
  const secInDay = 86400;
  const secInMonth = 30 * secInDay;
  const secInYear = 365.25 * secInDay;

  const years = Math.floor(seconds / secInYear);
  const months = Math.floor(seconds / secInMonth);
  const days = Math.floor(seconds / secInDay);
  const hours = Math.floor(seconds / secInHour);
  const minutes = Math.floor(seconds / secInMinute);
  const secs = Math.floor(seconds % 60);

  // === NEW CAP LOGIC ===
  let label = "";
  if (years >= 100) {
    label = "100+ years";
  } else if (years >= 50) {
    label = "50+ years";
  } else {
    const parts = [
      { v: years, u: "year" },
      { v: months, u: "month" },
      { v: days, u: "day" },
      { v: hours, u: "hour" },
      { v: minutes, u: "minute" },
      { v: secs, u: "second" },
    ].filter(p => p.v);

    label =
      parts.length === 0
        ? "0 seconds"
        : parts
            .slice(0, 2)
            .map(p => `${p.v} ${p.u}${p.v > 1 ? "s" : ""}`)
            .join(", ");
  }

  return {
    label,
    breakdown: { years, months, days, hours, minutes, seconds: secs },
  };
}


// function estimateCrackTimeDetailed(pw, guessesPerSecond = 1e9) {
//   if (!pw) return { seconds: 0, entropy: 0, avgGuesses: 0, guessesPerSecond, display: { label: "—", breakdown: null } };

//   function getCharsetSizeLocal(pw) {
//     let size = 0;
//     if (/[a-z]/.test(pw)) size += 26;
//     if (/[A-Z]/.test(pw)) size += 26;
//     if (/[0-9]/.test(pw)) size += 10;
//     if (/[!@#$%^&*()_\-+=[\]{}\\|;:'",.<>/?~`]/.test(pw)) size += 32;
//     if (/\s/.test(pw)) size += 1;
//     return Math.max(size, 1);
//   }

//   const L = pw.length;
//   const charset = getCharsetSizeLocal(pw);
//   const bitsPerChar = Math.log2(charset);
//   const entropy = Math.max(0, bitsPerChar * L);

//   const avgGuesses = Math.pow(2, Math.max(0, entropy - 1));
//   const seconds = avgGuesses / guessesPerSecond;

//   const display = formatDurationDetailed(seconds);

//   return { seconds, entropy, avgGuesses, guessesPerSecond, display };
// }


// Lightweight pattern-aware entropy estimator (keeps small and dependency-free)
function estimateEntropyBits(pw) {
  if (!pw) return 0;
  const COMMON_WORDS = ["password","123456","qwerty","omkar","admin","welcome","letmein","user","test","hello"];
  const lower = pw.toLowerCase();

  let bits = 0;
  let remaining = pw;

  // 1) dictionary/name detection (each hit ~13 bits)
  for (const w of COMMON_WORDS) {
    if (w.length < 3) continue;
    if (lower.includes(w)) {
      bits += 13; // ~8k-16k possibilities
      remaining = remaining.replace(new RegExp(w, "i"), "");
    }
  }

  // 2) numbers handling
  const numbers = (remaining.match(/\d+/g) || []);
  for (const num of numbers) {
    if (num.length <= 2) bits += Math.log2(100);        // ~6.64 bits
    else if (num.length <= 4) bits += Math.log2(10000); // ~13.29 bits
    else bits += num.length * Math.log2(10);
    remaining = remaining.replace(num, "");
  }

  // 3) repeated-run / simple sequence detection (small penalty)
  if (/(.)\1{2,}/.test(remaining)) {
    bits += 4;
    remaining = remaining.replace(/(.)\1{2,}/, "");
  }
  if (/(abc|bcd|cde|qwe|wer|ert|rty|yui|uiop)/i.test(remaining)) {
    bits += 6;
    remaining = remaining.replace(/(abc|bcd|cde|qwe|wer|ert|rty|yui|uiop)/i, "");
  }

  // 4) remaining chars: estimate by charset × length
  if (remaining.length > 0) {
    let charset = 0;
    if (/[a-z]/.test(remaining)) charset += 26;
    if (/[A-Z]/.test(remaining)) charset += 26;
    if (/[0-9]/.test(remaining)) charset += 10;
    if (/[^a-zA-Z0-9]/.test(remaining)) charset += 32;
    if (charset === 0) charset = 26;
    bits += remaining.length * Math.log2(charset);
  }

  return Math.max(0, bits);
}

// Re-usable duration formatter (keeps your caps/formatting behavior)
function formatDurationDetailed(totalSeconds) {
  if (!isFinite(totalSeconds) || totalSeconds <= 0) return { label: "<1s", breakdown: null };

  let remaining = Math.floor(totalSeconds);
  const secInMinute = 60;
  const secInHour = 3600;
  const secInDay = 86400;
  const secInMonth = 2592000; // 30 days
  const secInYear = 31557600; // 365.25 days

  const years = Math.floor(remaining / secInYear); remaining %= secInYear;
  const months = Math.floor(remaining / secInMonth); remaining %= secInMonth;
  const days = Math.floor(remaining / secInDay); remaining %= secInDay;
  const hours = Math.floor(remaining / secInHour); remaining %= secInHour;
  const minutes = Math.floor(remaining / secInMinute); remaining %= secInMinute;
  const seconds = remaining;

  const capYears = years >= 100 ? "100+" : years >= 50 ? "50+" : years;
  const capMonths = months > 99 ? "99+" : months;
  const capDays = days > 99 ? "99+" : days;
  const capHours = hours > 99 ? "99+" : String(hours).padStart(2, "0");
  const capMinutes = minutes > 99 ? "99+" : String(minutes).padStart(2, "0");
  const capSeconds = String(seconds).padStart(2, "0");

  const parts = [];
  if (typeof capYears === "string" ? capYears !== "0" : capYears > 0) parts.push(`${capYears}y`);
  if (parts.length < 2 && capMonths !== 0) parts.push(`${capMonths}m`);
  if (parts.length < 2 && capDays !== 0) parts.push(`${capDays}d`);
  if (parts.length < 2 && capHours !== "00") parts.push(`${capHours}h`);
  if (parts.length < 2 && capMinutes !== "00") parts.push(`${capMinutes}m`);
  if (parts.length === 0) parts.push(`${capSeconds}s`);

  const label = parts.slice(0, 2).join(" ");
  return {
    label,
    breakdown: {
      years: capYears,
      months: capMonths,
      days: capDays,
      hours: capHours,
      minutes: capMinutes,
      seconds: capSeconds,
    },
  };
}

// ===== Replacement: realistic estimator with same return shape =====
function estimateCrackTimeDetailed(pw, guessesPerSecond = 1e9) {
  if (!pw) {
    return { seconds: 0, entropy: 0, avgGuesses: 0, guessesPerSecond, display: { label: "—", breakdown: null } };
  }

  const entropy = estimateEntropyBits(pw);                // smarter entropy
  const avgGuesses = Math.pow(2, Math.max(0, entropy - 1)); // average guesses
  const seconds = avgGuesses / guessesPerSecond;
  const display = formatDurationDetailed(seconds);

  return {
    seconds,
    entropy: Number(entropy.toFixed(2)),
    avgGuesses,
    guessesPerSecond,
    display
  };
}


const menuItems = [
  { icon: RefreshCw, label: "Generator", path: "/passwordgenerator" },
  { icon: Clock, label: "Password History", path: "/passwordhistory" },
  { icon: Shield, label: "Advanced Password", path: "/advancedpassword" },
  // { icon: Settings, label: "Settings", path: "/settings" },
  // { icon: InfoIcon, label: "About", path: "/about" },
];







  return (


<div className="flex min-h-screen bg-gray-50 font-inter text-gray-800">

 <Sidebar menuItems={menuItems} />
 <SEOHelmet
  title="Password Generator"
  description="Generate strong, secure, and random passwords instantly with our free password generator tool. Customize length, numbers, and symbols for maximum protection."
  keywords="password generator, free password generator, secure password, random password, strong password, online password tool"
  url="https://omkarmore.com/passwordgenerator"
  image="https://omkarmore.com/LOGO_512.png"
/>


  {/* Main content */}
  <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10 space-y-10">
    {/* Top bar */}
    <div className="sticky top-0 bg-gray-50 z-20 py-4 flex justify-center md:justify-start px-6 shadow-sm">
      <button
        onClick={() => navigate("/tools")}
        className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Tools
      </button>
    </div>

    {/* Password Generator Section */}
    <section className="bg-white p-8 rounded-2xl shadow-lg space-y-8">
      <h1 className="text-3xl font-bold text-cyan-700">Generate Password</h1>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Left Settings */}
        <div className="flex-1 space-y-6">
          {/* Length */}
          {!useWords && !customBase && (
            <div>
              <label className="font-medium text-sm block mb-1">
                Password Length
              </label>
              <input
                type="number"
                min="4"
                max="64"
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2 w-24 text-center focus:ring-2 focus:ring-cyan-500"
                disabled={!!strengthLevel}
              />
            </div>
          )}

          {/* Options */}
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeUpper}
                onChange={(e) => setIncludeUpper(e.target.checked)}
                disabled={useWords}
              />
              Uppercase
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeNumbers}
                onChange={(e) => setIncludeNumbers(e.target.checked)}
              />
              Numbers
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeSymbols}
                onChange={(e) => setIncludeSymbols(e.target.checked)}
              />
              Symbols
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={excludeSimilar}
                onChange={(e) => setExcludeSimilar(e.target.checked)}
                disabled={useWords}
              />
              Exclude Similar
            </label>
          </div>

          {/* Word-based */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={useWords}
              onChange={(e) => {
                setUseWords(e.target.checked);
                setCustomBase("");
              }}
            />
            Word-Based Password
          </label>

          {/* Strength */}
          <div>
            <label className="font-medium text-sm block mb-2">
              Strength Level
            </label>
            <div className="flex gap-4 text-sm">
              {["Weak", "Medium", "Strong"].map((level) => (
                <label key={level} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="strengthLevel"
                    value={level}
                    checked={strengthLevel === level}
                    onChange={(e) => setStrengthLevel(e.target.value)}
                  />
                  {level}
                </label>
              ))}
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="strengthLevel"
                  value=""
                  checked={strengthLevel === ""}
                  onChange={() => {
                    setStrengthLevel("");
                    setPassword("");
                    setCustomBase("");
                    setCopied(false);
                    setUseWords(false);
                    setLength(12);
                    setIncludeUpper(true);
                    setIncludeNumbers(true);
                    setIncludeSymbols(false);
                    setExcludeSimilar(false);
                  }}
                />
                Normal
              </label>
            </div>
          </div>

          {/* Custom Base */}
          <div>
            <label className="font-medium text-sm block">
              Custom Base Word / Phrase
            </label>
            <input
              type="text"
              value={customBase}
              onChange={(e) => {
                setCustomBase(e.target.value);
                setUseWords(false);
              }}
              placeholder="e.g. Abc@2025"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-2 focus:ring-2 focus:ring-cyan-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              We’ll enhance your base with numbers/symbols for extra security.
            </p>
          </div>

          {/* Generate */}
          <button
            onClick={generatePassword}
            className="w-full px-6 py-3 bg-cyan-700 text-white font-semibold rounded-xl hover:bg-cyan-600 transition flex items-center justify-center gap-2 shadow-sm"
          >
            <RefreshCw className="w-5 h-5" /> Generate
          </button>
        </div>

        {/* Right Output */}
        <div className="flex-1 bg-gray-50 p-6 rounded-2xl shadow-inner flex flex-col items-center justify-center text-center space-y-6">
          <h2 className="text-xl font-bold text-cyan-700">Your Password</h2>
          {/* If generated */}
          {password ? (
            <>
              <p
                className={`text-lg font-mono ${
                  showPassword ? "tracking-normal" : "tracking-widest"
                }`}
              >
                {showPassword ? password : "•".repeat(password.length)}
              </p>
              {/* Strength Bar */}
              <div className="w-full bg-gray-200 h-2 rounded-full">
                <div
                  className={`h-2 rounded-full ${
                    getStrength() === "Weak"
                      ? "bg-red-500 w-1/3"
                      : getStrength() === "Medium"
                      ? "bg-yellow-500 w-2/3"
                      : "bg-green-600 w-full"
                  }`}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                Strength:{" "}
                <span className="font-bold">{getStrength()}</span>
              </p>

              {/* Actions */}
              <div className="mt-4 flex flex-wrap gap-4 justify-center">
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-cyan-700 hover:text-cyan-600 text-sm font-medium flex items-center gap-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPassword ? "Hide" : "Show"}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="text-cyan-700 hover:text-cyan-600 text-sm font-medium flex items-center gap-1"
                >
                  <Copy className="w-4 h-4" /> {copied ? "Copied!" : "Copy"}
                </button>
                <button
                  onClick={() => {
                    if (!password) return;
                    const now = new Date();
                    const content = `Password Generated:\n${password}\n\nGenerated On: ${now.toLocaleString()}`;
                    const blob = new Blob([content], {
                      type: "text/plain;charset=utf-8",
                    });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `password_${now.getTime()}.txt`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="text-cyan-700 hover:text-cyan-600 text-sm font-medium flex items-center gap-1"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-sm">
              Click <span className="font-medium">Generate</span> to create your password
            </p>
          )}

          {/* Manual Strength Checker */}
 <div className="w-full border-t border-gray-200 pt-6">
      <h2 className="text-lg font-semibold text-cyan-700 mb-3">
        Check Password Strength
      </h2>

      <input
        type="text"
        placeholder="Enter any password"
        value={passwordInput}
        onChange={(e) => setPasswordInput(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-cyan-500"
      />

      {passwordInput && (
        <>
          {/* Strength bar */}
          <div className="w-full bg-gray-200 h-2 rounded-full mb-2">
            <div
              className={`h-2 rounded-full ${
                getStrength(passwordInput) === "Weak"
                  ? "bg-red-500 w-1/3"
                  : getStrength(passwordInput) === "Medium"
                  ? "bg-yellow-500 w-2/3"
                  : "bg-green-600 w-full"
              }`}
            ></div>
          </div>

          <p className="text-sm text-gray-600 mb-1">
            Strength:{" "}
            <span className="font-bold">{getStrength(passwordInput)}</span>
          </p>

          {/* Recommendations */}
          <p className="text-xs text-gray-500 mb-3">
            {getStrength(passwordInput) === "Weak" &&
              "Add uppercase, numbers, symbols, or increase length."}
            {getStrength(passwordInput) === "Medium" &&
              "Add symbols or increase length for stronger password."}
            {getStrength(passwordInput) === "Strong" && "Your password is strong!"}
          </p>

          {/* Estimated time to crack */}
          <div className="mt-2 w-full">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-sm text-gray-600">
                Estimated time to crack:
                <span className="font-bold ml-2">{display.label}</span>
              </p>
              <p className="text-xs text-gray-400">
                Entropy:{" "}
                <span className="font-mono font-semibold">
                  {Math.round(entropy)} bits
                </span>
              </p>
            </div>

            <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden mb-2">
              <div
                className={`${barColor} h-3`}
                style={{ width: `${widthPercent}%`, transition: "width 300ms" }}
              />
            </div>

            <p className="text-xs text-gray-400 mb-2">
              (Assumes{" "}
              <span className="font-mono">
                {Number(guessesPerSecond).toExponential()}
              </span>{" "}
              guesses/sec — avg guesses ≈{" "}
              <span className="font-mono">
                {Number(avgGuesses).toExponential(2)}
              </span>
              )
            </p>

            {/* Detailed breakdown */}
{display.breakdown && (
  <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
    <div className="mb-2 font-medium text-gray-800">Detailed Time</div>

    {/* Grid layout */}
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
      {/* Years */}
      <div>
        <div className="text-[11px] text-gray-500">Years</div>
        <div className="font-mono font-semibold">
          {display.breakdown.years >= 100
            ? "100+"
            : display.breakdown.years >= 50
            ? "50+"
            : display.breakdown.years}
        </div>
      </div>

      {/* Months */}
      <div>
        <div className="text-[11px] text-gray-500">Months</div>
        <div className="font-mono font-semibold">
          {display.breakdown.months > 99 ? "99+" : display.breakdown.months}
        </div>
      </div>

      {/* Days */}
      <div>
        <div className="text-[11px] text-gray-500">Days</div>
        <div className="font-mono font-semibold">
          {display.breakdown.days > 99 ? "99+" : display.breakdown.days}
        </div>
      </div>

      {/* Hours */}
      <div>
        <div className="text-[11px] text-gray-500">Hours</div>
        <div className="font-mono font-semibold">
          {display.breakdown.hours > 99
            ? "99+"
            : display.breakdown.hours.toString().padStart(2, "0")}
        </div>
      </div>

      {/* Minutes */}
      <div>
        <div className="text-[11px] text-gray-500">Minutes</div>
        <div className="font-mono font-semibold">
          {display.breakdown.minutes > 99
            ? "99+"
            : display.breakdown.minutes.toString().padStart(2, "0")}
        </div>
      </div>

      {/* Seconds */}
      <div>
        <div className="text-[11px] text-gray-500">Seconds</div>
        <div className="font-mono font-semibold">
          {display.breakdown.seconds.toString().padStart(2, "0")}
        </div>
      </div>
    </div>

    {/* Footnote */}
    {/* <div className="mt-3 text-[11px] text-gray-500 text-center">
      Values capped at <code>50+</code>, <code>100+</code>, <code>99+</code> for readability.
    </div> */}

    {/* Suggestions */}
    {/* <div className="mt-4 p-3 bg-white border border-gray-100 rounded-md text-gray-600">
      <div className="font-medium text-gray-800 mb-1">What to improve:</div>
      <ul className="list-disc pl-5 space-y-1 text-[12px]">
        {!/[A-Z]/.test(passwordInput) && (
          <li>Add <b>uppercase letters</b> (A–Z)</li>
        )}
        {!/[0-9]/.test(passwordInput) && (
          <li>Add <b>numbers</b> (0–9)</li>
        )}
        {!/[!@#$%^&*()_\-+=[\]{}\\|;:'",.<>/?~`]/.test(passwordInput) && (
          <li>Add <b>symbols</b> (!@#$%^&amp;*)</li>
        )}
        {passwordInput.length < 12 && (
          <li>Increase length (at least <b>12 characters</b>)</li>
        )}
        {passwordInput.length >= 12 &&
          /[A-Z]/.test(passwordInput) &&
          /[0-9]/.test(passwordInput) &&
          /[!@#$%^&*()_\-+=[\]{}\\|;:'",.<>/?~`]/.test(passwordInput) && (
          <li className="text-green-600">Your password is strong 🎉</li>
        )}
      </ul>
    </div> */}
  </div>
)}



          </div>
        </>
      )}
    </div>

        </div>
      </div>
    </section>

    {/* Footer */}
    {/* <footer className="w-full bg-gray-100 py-6 text-center text-gray-600 text-sm">
      © {new Date().getFullYear()} Omkar More · All Rights Reserved.
    </footer> */}
  </main>
</div>




  );
}
